import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import { DECLINE_REASON_OPTIONS } from '../../../constants/patientLifecycle';
import type { DeclineReason } from '../../../dtos/clinical/referral.dto';

export interface DeclineReferralInput {
  declineReason: DeclineReason;
  declineNote?: string;
}

export interface DeclineReferralDialogProps {
  open: boolean;
  busy?: boolean;
  /** Shown when declining the only referral of a pending patient (the shell becomes inactive) */
  pendingShellNotice?: boolean;
  onConfirm: (input: DeclineReferralInput) => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Decline a referral (REF-05): a reason from the PRD enum is required, and a note is required
 * when the reason is Other.
 */
const DeclineReferralDialog: React.FC<DeclineReferralDialogProps> = ({
  open,
  busy = false,
  pendingShellNotice = false,
  onConfirm,
  onCancel,
}) => {
  const baseId = useId();
  const [reason, setReason] = useState<DeclineReason | ''>('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setNote('');
    setSubmitted(false);
  }, [open]);

  const reasonMissing = !reason;
  const noteMissing = reason === 'OTHER' && !note.trim();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (reasonMissing || noteMissing || busy || !reason) return;
    void onConfirm({ declineReason: reason, declineNote: note.trim() || undefined });
  };

  const titleId = `${baseId}-title`;
  const reasonId = `${baseId}-reason`;
  const noteId = `${baseId}-note`;

  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} labelledBy={titleId}>
      <form onSubmit={handleSubmit} className="text-left" noValidate>
        <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">
          Decline referral
        </h3>
        {pendingShellNotice && (
          <p className="mt-1 text-sm text-slate-600">
            If this is the patient's only referral, the pending patient becomes inactive and leaves the census.
          </p>
        )}
        <label htmlFor={reasonId} className="block mt-4 text-sm font-medium text-slate-700">
          Decline reason <span className="text-red-600" aria-hidden="true">*</span>
        </label>
        <select
          id={reasonId}
          value={reason}
          onChange={(e) => setReason(e.target.value as DeclineReason | '')}
          disabled={busy}
          aria-invalid={submitted && reasonMissing}
          className="mt-1 w-full rounded-md border border-slate-300 bg-white p-2 text-sm"
        >
          <option value="">Select a reason…</option>
          {DECLINE_REASON_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {submitted && reasonMissing && <p className="mt-1 text-xs text-red-700">Select a decline reason.</p>}

        <label htmlFor={noteId} className="block mt-3 text-sm font-medium text-slate-700">
          Note {reason === 'OTHER' && <span className="text-red-600" aria-hidden="true">*</span>}
        </label>
        <textarea
          id={noteId}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={1000}
          disabled={busy}
          aria-invalid={submitted && noteMissing}
          placeholder={reason === 'OTHER' ? 'Required for Other' : 'Optional'}
          className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
        />
        {submitted && noteMissing && <p className="mt-1 text-xs text-red-700">A note is required when the reason is Other.</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
          >
            {busy ? 'Declining…' : 'Decline referral'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default DeclineReferralDialog;
